<template>
    <div
        :class="{
            'dk-chip': true,
            [`dk-chip--${variant}`]: variant,
            [`dk-chip--${color}`]: color,
            'dk-chip--active': active,
            'dk-chip--disabled': disabled,
            'dk-chip--clickable': clickable,
            'dk-chip--deletable': deletable,
        }"
        @click="onClick"
    >
        <div class="dk-chip__wrapper" :class="{ '!tw-border-0 !tw-bg-transparent': hasTogglerStyle }">
            <div
                class="dk-chip__text ts--caption p-top--xxs p-bottom--xxs p-right--xs p-left--xs"
                :class="{ '!tw-underline !tw-font-bold': hasTogglerStyle }"
            >
                <deferred-img
                    v-if="iconUrl"
                    :src="iconUrl"
                    :alt="item.name"
                    :title="item.name"
                    class="dk-chip__img"
                />
                <template v-else>
                    <icon
                        v-if="hasIcon"
                        :name="iconName"
                        :size="iconSize"
                        :color="iconColor"
                        class="dk-chip__icon m-right--xxs"
                    />
                    <!-- @slot Chip content -->
                    <slot></slot>
                </template>
                <icon v-if="deletable" name="generic-close--big" size="xxs" class="dk-chip__icon m-left--xs" />
            </div>
        </div>
    </div>
</template>

<script>
/**
 * Standalone chip
 *
 * @version 4.0.0
 * @example ./examples/DkChip.md
 */
export default {
    name: 'DkChip',
    components: {
        Icon: () => ({
            component: import(/* webpackChunkName: "Icon-vue" */ '@v4/common/components/atoms/Icon.vue'),
        }),
        DeferredImg: () => ({
            component: import(/* webpackChunkName: "DeferredImg-vue" */ '@v4/common/components/atoms/DeferredImg.vue'),
        }),
    },
    props: {
        /**
         * Active
         */
        active: {
            type: Boolean,
            default: false,
        },
        /**
         * Clickable
         */
        clickable: {
            type: Boolean,
            default: false,
        },
        /**
         * Deletable
         */
        deletable: {
            type: Boolean,
            default: false,
        },
        /**
         * Disabled
         */
        disabled: {
            type: Boolean,
            default: false,
        },
        /**
         * Chip item
         */
        item: {
            type: Object,
            default: () => ({}),
        },
        /**
         * UI variants. Available values: `outlined`. Default: ``
         */
        variant: {
            type: String,
            default: '',
            validator: (value) => ['', 'outlined'].includes(value),
        },
        /**
         * Chip color. Available: `turquoise`
         */
        color: {
            type: String,
            default: '',
        },
        /**
         * Has toggler style (without border)
         */
        hasTogglerStyle: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        hasIcon() {
            return !!this.item.icon;
        },
        iconName() {
            return (this.hasIcon && this.item.icon.name) || '';
        },
        iconUrl() {
            return (this.hasIcon && this.item.icon.url) || '';
        },
        iconSize() {
            return (this.hasIcon && this.item.icon.size) || 'xs';
        },
        iconColor() {
            return (this.hasIcon && this.item.icon.color) || '';
        },
    },
    methods: {
        onClick($event) {
            if (this.disabled || !this.clickable) {
                return;
            }

            /**
             * Fired when clicked on the chip item
             *
             * @event click
             * @type {object}
             * @property {object} $event - Click event
             * @property {object} item - Item object
             * @property {object} DkChip - DkChip vue component object
             */
            this.$emit('click', {
                $event,
                item: this.item,
                DkChip: this,
            });
        },
    },
};
</script>
